import { getRandomInt } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import {
    BoxSpec,
    JoinedBoxesSpec,
    ProblemData,
    SOLID_VOLUME_QUESTIONS,
    SolidKind,
    SolidSpec,
    SUPERSCRIPT,
    VolumeQuestionOptions,
} from "./Constants.ts";
import { PI_HUNDREDTHS } from "./CircleGenerator.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import { volumePrompts as en } from "../Files/Prompts/en/volume.ts";
import { volumePrompts as es } from "../Files/Prompts/es/volume.ts";
import { volumePrompts as fr } from "../Files/Prompts/fr/volume.ts";

const PROMPTS: Record<PrintedLanguage, typeof en> = { en, es, fr };

/**
 * Volume of rectangular boxes. See ADR 0095.
 *
 * Two rules shape this file, and both protect a *drawing* with a constraint on
 * *content*, as ShapeGenerator's 4x rule does (ADR 0038 §2):
 *
 *   1. No two labelled edges differ by more than a factor of four. A
 *      2 x 2 x 12 box is a stick, and its depth label would have nowhere to
 *      sit (ADR 0095 §2). The bound is held by *redrawing*, not by
 *      construction: each edge is drawn independently and uniformly over
 *      2..max, and a triple whose longest edge is more than four times its
 *      shortest is thrown away and drawn again. Building the edges around a
 *      shortest edge drawn first (as ShapeGenerator does for two sides) made
 *      the bound free but skewed the boxes - at a longest edge of 5 a quarter
 *      of them were 5 x 5 x 5 - so big cubes dominated a sheet. Redrawing
 *      gives an even spread of shapes. The loop is capped; if the cap is
 *      ever reached the triple is clamped into the ratio, so the rule holds
 *      on every path and generation stays synchronous.
 *   2. Counting keeps every edge between 2 and 5. The picture shows only the
 *      visible faces, the prompt says the box is full, and past 5 the cells
 *      are too small to count on a half sheet (ADR 0095 §3).
 *
 * Neither is visible in the output. `VolumeGenerator.test.ts` is what says so.
 *
 * Two boxes (ADR 0097) follows the same two ideas for a solid of two boxes: the
 * whole solid's longest extent (the two widths added, or the taller height) is
 * held to four times its shortest edge by redrawing, and the two heights are
 * drawn at least two apart by the same redraw, so the step is visible. It is
 * outside Mixed, so it draws from the generator only when asked for.
 *
 * Surface area (ADR 0100) is outside Mixed too. Each problem draws its edges as
 * Edge lengths does (2..max, within 4:1), and each printed page alternates two
 * drawings of the box, a flat net and the labelled box, balanced by the same
 * fair sequence Mixed uses, so half of each and an odd count differs by one.
 * The balance is per page, not per sheet: each page, and each half of a half
 * page, goes to a different student.
 *
 * Cylinders, cones and spheres (ADR 0140) are outside Mixed as well. Radius and
 * height are whole numbers from 2 to the dial, and the answer is integer
 * hundredths with pi as 314: a cylinder's 314 r^2 h is whole as it stands, a
 * cone's third of it is whole when the radius or the height is a multiple of 3,
 * and a sphere's four thirds of 314 r^3 when the radius is. So the cone and
 * sphere are *restricted*, not rounded, and every answer is exact with at most two
 * decimals. A cylinder or cone also keeps its diameter and height within 4:1, the
 * box's rule, so the drawing's height label has room. Each is held by choosing
 * from the list of pairs that satisfy it (at most 11 x 11), which draws the same
 * way as redrawing until one does but cannot fail to finish. Cylinders, cones
 * and spheres deals the three solids evenly over each printed page.
 *
 * Mixed is balanced by `generateProblemPages` over the three question types
 * (ADR 0041), so a page that counts a box and then multiplies its edges is the
 * same box drawn both ways (5.MD.C.5a).
 */

/** ADR 0095 §2: beyond this a labelled box is a stick. */
export const MAX_EDGE_RATIO = 4;

/** Below this a "box" is a sheet of paper at any drawable size. */
const SMALLEST_EDGE = 2;

/**
 * Redraws of a triple that breaks the ratio before giving up and clamping.
 * Even at a longest edge of 12 most triples are accepted, so this is never
 * reached in practice; it only keeps the loop finite.
 */
const REDRAW_LIMIT = 100;

/** ADR 0095 §3: the counting question's edges. */
export const COUNT_MIN_EDGE = 2;
export const COUNT_MAX_EDGE = 5;

/** ADR 0097 §4: with equal heights the solid is one box with a line through it. */
export const MIN_STEP = 2;

/**
 * The solid used if `REDRAW_LIMIT` is ever reached: small, and valid under every
 * rule in ADR 0097 §4 (heights 2 apart, longest extent 4 against a shortest
 * edge of 2). Fixed rather than random because reaching it is a one-in-10^15
 * event; what matters is that the rules hold on every path.
 */
const FALLBACK_JOINED: JoinedBoxesSpec = {
    depth: 2,
    left: { width: 2, height: 2 },
    right: { width: 2, height: 4 },
};

const VOLUME_UNIT = `cm${SUPERSCRIPT[3]}`;
const AREA_UNIT = `cm${SUPERSCRIPT[2]}`;
export const LENGTH_UNIT = "cm";
const UNKNOWN = "?";

/** ADR 0100 §2: how a Surface Area problem draws its box. */
export type SurfaceAreaDrawing = "net" | "box";

const SURFACE_AREA_DRAWINGS: SurfaceAreaDrawing[] = ["net", "box"];

function questionTypes(question: VolumeQuestionOptions): VolumeQuestionOptions[] {
    if (question === VolumeQuestionOptions.Mixed) {
        return [
            VolumeQuestionOptions.CountCubes,
            VolumeQuestionOptions.EdgeLengths,
            VolumeQuestionOptions.MissingEdge,
        ];
    }
    return [question];
}

function usableMax(maxEdge: number): number {
    return Math.max(SMALLEST_EDGE, Math.floor(maxEdge) || 10);
}

/**
 * Three edges within the drawable ratio, each drawn independently and
 * uniformly over 2..max, so which edge is the length, width or height needs no
 * shuffle and a sheet is not all tall boxes.
 *
 * A triple outside 4:1 is redrawn. After `REDRAW_LIMIT` misses the edges are
 * clamped to four times the shortest, which is within the ratio by
 * construction.
 */
function edges(max: number): [number, number, number] {
    const draw = (): [number, number, number] => [
        getRandomInt(SMALLEST_EDGE, max),
        getRandomInt(SMALLEST_EDGE, max),
        getRandomInt(SMALLEST_EDGE, max),
    ];
    let triple = draw();
    for (let redraws = 0; redraws < REDRAW_LIMIT && !withinRatio(triple); redraws++) {
        triple = draw();
    }
    if (!withinRatio(triple)) {
        const limit = Math.min(...triple) * MAX_EDGE_RATIO;
        triple = triple.map((edge) => Math.min(edge, limit)) as [number, number, number];
    }
    return triple;
}

function withinRatio(triple: number[]): boolean {
    return Math.max(...triple) <= MAX_EDGE_RATIO * Math.min(...triple);
}

function joinedWithinRules(solid: JoinedBoxesSpec): boolean {
    const { depth, left, right } = solid;
    if (Math.abs(left.height - right.height) < MIN_STEP) {
        return false;
    }
    const longest = Math.max(left.width + right.width, left.height, right.height);
    const shortest = Math.min(depth, left.width, left.height, right.width, right.height);
    return longest <= MAX_EDGE_RATIO * shortest;
}

/**
 * Two boxes side by side: each width, each height and the depth drawn
 * independently and uniformly over 2..max, and the solid redrawn until it
 * keeps the step and the 4:1 rule (ADR 0097 §4). About three in ten solids
 * pass, so the cap is never reached in practice.
 */
function joinedBoxes(max: number): JoinedBoxesSpec {
    const draw = (): JoinedBoxesSpec => ({
        depth: getRandomInt(SMALLEST_EDGE, max),
        left: { width: getRandomInt(SMALLEST_EDGE, max), height: getRandomInt(SMALLEST_EDGE, max) },
        right: { width: getRandomInt(SMALLEST_EDGE, max), height: getRandomInt(SMALLEST_EDGE, max) },
    });
    let solid = draw();
    for (let redraws = 0; redraws < REDRAW_LIMIT && !joinedWithinRules(solid); redraws++) {
        solid = draw();
    }
    return joinedWithinRules(solid) ? solid : FALLBACK_JOINED;
}

const SOLID_KINDS: SolidKind[] = ["cylinder", "cone", "sphere"];

const SOLID_OF_QUESTION: Partial<Record<VolumeQuestionOptions, SolidKind>> = {
    [VolumeQuestionOptions.Cylinders]: "cylinder",
    [VolumeQuestionOptions.Cones]: "cone",
    [VolumeQuestionOptions.Spheres]: "sphere",
};

/** ADR 0140 §3: a sphere's radius is a multiple of this, so its four thirds comes out even. */
const SPHERE_RADIUS_STEP = 3;

/**
 * Whether a diameter and a height are drawable together: within 4:1 either way
 * (ADR 0140 §3, ADR 0095 §2's rule).
 */
function solidWithinRatio(radius: number, height: number): boolean {
    const diameter = 2 * radius;
    return Math.max(diameter, height) <= MAX_EDGE_RATIO * Math.min(diameter, height);
}

/** Every radius (and height) a solid may have at this dial (ADR 0140 §3). */
export function solidDimensions(kind: SolidKind, max: number): { radius: number; height?: number }[] {
    const all: { radius: number; height?: number }[] = [];
    if (kind === "sphere") {
        // At a dial of 5 only 3 is a multiple of 3, so the sheet reaches 6: the
        // one place the dial is exceeded.
        const top = Math.max(max, 2 * SPHERE_RADIUS_STEP);
        for (let radius = SPHERE_RADIUS_STEP; radius <= top; radius += SPHERE_RADIUS_STEP) {
            all.push({ radius });
        }
        return all;
    }
    for (let radius = SMALLEST_EDGE; radius <= max; radius++) {
        for (let height = SMALLEST_EDGE; height <= max; height++) {
            if (!solidWithinRatio(radius, height)) {
                continue;
            }
            if (kind === "cone" && radius % 3 !== 0 && height % 3 !== 0) {
                continue;
            }
            all.push({ radius, height });
        }
    }
    return all;
}

/**
 * A solid's volume in hundredths of a cm cubed, with pi as 3.14 (ADR 0140 §2):
 * cylinder 314 r^2 h, cone a third of that, sphere four thirds of 314 r^3. The
 * dimensions are restricted so the third is whole; if one is not, this throws
 * rather than print a rounded answer.
 */
export function solidVolumeHundredths(kind: SolidKind, radius: number, height = 0): number {
    const numerator =
        kind === "sphere" ? 4 * PI_HUNDREDTHS * radius ** 3 : PI_HUNDREDTHS * radius * radius * height;
    const divisor = kind === "cylinder" ? 1 : 3;
    if (numerator % divisor !== 0) {
        throw new Error(`The volume of a ${kind} with radius ${radius} and height ${height} is not whole hundredths`);
    }
    return numerator / divisor;
}

/**
 * One cylinder, cone or sphere. Always succeeds. The radius is chosen from the
 * dimensions that satisfy the solid's rules, not `avoidRadius` (the radius of
 * the solid before it, if that was the same kind); with nothing else to choose
 * (it cannot happen at the dials offered) it repeats.
 */
export function buildSolidProblem(
    kind: SolidKind,
    maxEdge: number,
    key: number,
    language: PrintedLanguage = "en",
    avoidRadius?: number,
    question: VolumeQuestionOptions = VolumeQuestionOptions.Solids
): ProblemData {
    const t = PROMPTS[language];
    const candidates = solidDimensions(kind, usableMax(maxEdge));
    const fresh = candidates.filter((candidate) => candidate.radius !== avoidRadius);
    const pool = fresh.length > 0 ? fresh : candidates;
    const { radius, height } = pool[getRandomInt(0, pool.length - 1)];
    // Radius or diameter given, half each, as Circles does (ADR 0106 §2).
    const given: SolidSpec["given"] = getRandomInt(0, 1) === 0 ? "radius" : "diameter";
    const volume = solidVolumeHundredths(kind, radius, height);
    return {
        key,
        numbers: [],
        operator: "",
        display: [t[kind](), `${t.decimal(volume)} ${VOLUME_UNIT}`],
        solid: { kind, radius, ...(height === undefined ? {} : { height }), given },
        volumeQuestion: question,
    };
}

/**
 * One volume problem. Always succeeds.
 *
 * The box rides on its own `ProblemData` field. `display` carries the prompt
 * and the answer, and the answer includes its unit because the unit is the
 * part this topic exists to teach (ADR 0095 §5).
 */
export function buildVolumeProblem(
    question: VolumeQuestionOptions,
    maxEdge: number,
    key: number,
    language: PrintedLanguage = 'en',
    drawing: SurfaceAreaDrawing = 'box'
): ProblemData {
    const t = PROMPTS[language];

    if (SOLID_VOLUME_QUESTIONS.includes(question)) {
        // Cylinders, cones and spheres name their solid; the dealt type has no
        // single one here, so it picks (the page generator deals them instead).
        const kind = SOLID_OF_QUESTION[question] ?? SOLID_KINDS[getRandomInt(0, SOLID_KINDS.length - 1)];
        return buildSolidProblem(kind, maxEdge, key, language, undefined, question);
    }

    if (question === VolumeQuestionOptions.CountCubes) {
        const length = getRandomInt(COUNT_MIN_EDGE, COUNT_MAX_EDGE);
        const width = getRandomInt(COUNT_MIN_EDGE, COUNT_MAX_EDGE);
        const height = getRandomInt(COUNT_MIN_EDGE, COUNT_MAX_EDGE);
        return {
            key,
            numbers: [],
            operator: "",
            display: [t.countCubes(), t.cubes(length * width * height)],
            box: { length, width, height, cubes: true },
            volumeQuestion: question,
        };
    }

    if (question === VolumeQuestionOptions.TwoBoxes) {
        const solid = joinedBoxes(usableMax(maxEdge));
        const total =
            solid.left.width * solid.depth * solid.left.height +
            solid.right.width * solid.depth * solid.right.height;
        return {
            key,
            numbers: [],
            operator: "",
            display: [t.twoBoxes(), `${total} ${VOLUME_UNIT}`],
            joinedBoxes: solid,
            volumeQuestion: question,
        };
    }

    const [length, width, height] = edges(usableMax(maxEdge));
    const volume = length * width * height;
    const box: BoxSpec = {
        length,
        width,
        height,
        lengthLabel: `${length} ${LENGTH_UNIT}`,
        widthLabel: `${width} ${LENGTH_UNIT}`,
        heightLabel: `${height} ${LENGTH_UNIT}`,
    };

    if (question === VolumeQuestionOptions.SurfaceArea) {
        const area = 2 * (length * width + length * height + width * height);
        const isNet = drawing === 'net';
        return {
            key,
            numbers: [],
            operator: "",
            display: [isNet ? t.surfaceAreaNet() : t.surfaceAreaBox(), `${area} ${AREA_UNIT}`],
            ...(isNet ? { boxNet: box } : { box }),
            volumeQuestion: question,
        };
    }

    if (question === VolumeQuestionOptions.MissingEdge) {
        // Answer-first: all three edges are drawn, the volume computed, then
        // one edge hidden - so the division a student does is exact by
        // construction.
        const hidden = getRandomInt(0, 2);
        const edge = [length, width, height][hidden];
        if (hidden === 0) {
            box.lengthLabel = UNKNOWN;
        } else if (hidden === 1) {
            box.widthLabel = UNKNOWN;
        } else {
            box.heightLabel = UNKNOWN;
        }
        return {
            key,
            numbers: [],
            operator: "",
            display: [t.missingEdge(`${volume} ${VOLUME_UNIT}`), `${edge} ${LENGTH_UNIT}`],
            box,
            volumeQuestion: question,
        };
    }

    return {
        key,
        numbers: [],
        operator: "",
        display: [t.edgeLengths(), `${volume} ${VOLUME_UNIT}`],
        box,
        volumeQuestion: question,
    };
}

// The same box asked the same way is the same problem however it was
// drawn. Which edge is missing matters: it is a different division. A net and
// the box it folds into are different pictures, so they are different problems.
function fingerprint(problem: ProblemData): string {
    if (problem.solid) {
        // Radius or diameter is how it is labelled, a different problem to a student.
        const { kind, radius, height, given } = problem.solid;
        return `${kind}:${radius}x${height ?? ""}:${given}`;
    }
    if (problem.joinedBoxes) {
        const { depth, left, right } = problem.joinedBoxes;
        return `${problem.volumeQuestion}:${left.width}x${left.height}+${right.width}x${right.height}x${depth}`;
    }
    const net = problem.boxNet;
    const box = (net ?? problem.box)!;
    const labels = `${box.lengthLabel ?? ""}|${box.widthLabel ?? ""}|${box.heightLabel ?? ""}`;
    return `${problem.volumeQuestion}:${net ? "net:" : ""}${box.length}x${box.width}x${box.height}:${labels}`;
}

/** A page-shaped set of volume problems. */
export function generateVolumePages(settings: ISettingsProps): ProblemData[][] | null {
    const question = settings.VolumeQuestion ?? VolumeQuestionOptions.Mixed;
    const maxEdge = settings.VolumeMaxEdge ?? 10;
    const language = printedLanguageOf(settings);
    if (SOLID_VOLUME_QUESTIONS.includes(question)) {
        // Each printed page, and each half of a half page, deals the three
        // solids evenly for Solids (ADR 0140 §1), as 0139 dealt its kinds.
        // `build` is called again at a key until a problem is accepted there,
        // so the last radius recorded for key k is the one that stays, and
        // key k + 1 avoids it when it is the same solid (§3).
        const solidAt = new Map<number, { kind: SolidKind; radius: number }>();
        const only = SOLID_OF_QUESTION[question];
        return generateProblemPages({
            settings,
            choices: only ? [only] : SOLID_KINDS,
            build: (kind, key) => {
                const before = solidAt.get(key - 1);
                const problem = buildSolidProblem(
                    kind,
                    maxEdge,
                    key,
                    language,
                    before?.kind === kind ? before.radius : undefined,
                    question
                );
                solidAt.set(key, { kind, radius: problem.solid!.radius });
                return problem;
            },
            fingerprint,
            dealPerPage: only === undefined,
        });
    }
    if (question === VolumeQuestionOptions.SurfaceArea) {
        // Balanced page by page, not across the sheet: each printed page, and
        // each half of a half page (one call here per half), goes to a student
        // and must hold nets and boxes as evenly as it can (ADR 0100 §2).
        const perPage = settings.NumberOfProblemsPerPage;
        const pages: ProblemData[][] = [];
        for (let page = 0; page < settings.NumberOfPages; page++) {
            const onePage = generateProblemPages({
                settings: { ...settings, NumberOfPages: 1 },
                choices: SURFACE_AREA_DRAWINGS,
                build: (drawing, key) => buildVolumeProblem(question, maxEdge, key, language, drawing),
                fingerprint,
            });
            if (onePage === null) {
                return pages.length === 0 ? null : pages;
            }
            // Keys number the whole sheet, not the page they were drawn on.
            pages.push(onePage[0].map((problem) => ({ ...problem, key: page * perPage + problem.key })));
        }
        return pages;
    }
    return generateProblemPages({
        settings,
        choices: questionTypes(question),
        build: (type, key) => buildVolumeProblem(type, maxEdge, key, language),
        fingerprint,
    });
}
